import type { IBankRequestCacheRepository } from '@app/domain/ports';
import type { ICryptoAdapter } from '@app/domain/ports';
import type { IBankStatementCachePort } from '@app/domain/ports';
import type { IDekContainer } from '@app/domain/ports';
import { CachedBankStatement, DekError } from '@app/domain/entities';

interface EncryptedStatements {
  ciphertext: Uint8Array;
  iv: Uint8Array;
}

/**
 * Infrastructure adapter that wraps `IBankRequestCacheRepository` with
 * optional AES-GCM encryption/decryption using the Data Encryption Key (DEK).
 *
 * Pure TypeScript class – no `@Injectable`, no `inject()`.
 * All dependencies are supplied via constructor parameters so that the class
 * can be wired up through a factory provider in `infrastructure.providers.ts`.
 */
export class EncryptedBankCacheAdapter implements IBankStatementCachePort {
  constructor(
    private readonly bankCacheRepo: IBankRequestCacheRepository,
    private readonly crypto: ICryptoAdapter,
    private readonly getDekContainer: () => IDekContainer,
    private readonly isEncryptionEnabled: () => boolean,
  ) {}

  // ─── Private helpers ───────────────────────────────────────────────────────

  private async encryptStatements(statements: CachedBankStatement[]): Promise<EncryptedStatements> {
    const jsonString = JSON.stringify(statements);
    let result: EncryptedStatements | null = null;

    await this.getDekContainer().execute(async (dek) => {
      if (!dek) {
        throw new DekError('encrypt', 'Encryption key not available');
      }

      const { ciphertext, iv } = await this.crypto.encryptString(jsonString, dek);
      result = { ciphertext, iv };
    });

    if (!result) {
      throw new DekError('encrypt', 'Encryption failed');
    }

    return result;
  }

  private async decryptStatements(encrypted: EncryptedStatements): Promise<CachedBankStatement[]> {
    let result: CachedBankStatement[] | null = null;

    await this.getDekContainer().execute(async (dek) => {
      if (!dek) {
        throw new DekError('decrypt', 'Decryption key not available');
      }

      const plaintext = await this.crypto.decryptString(encrypted.ciphertext, encrypted.iv, dek);
      result = JSON.parse(plaintext);
    });

    if (!result) {
      throw new DekError('decrypt', 'Decryption failed');
    }

    return result;
  }

  // ─── IBankStatementCachePort ───────────────────────────────────────────────

  /**
   * Save a new cache entry with optional encryption.
   */
  async saveRange<TStatement extends CachedBankStatement>(
    source: string,
    accountId: string,
    fromTimestamp: number,
    toTimestamp: number,
    statements: TStatement[],
  ): Promise<void> {
    if (this.isEncryptionEnabled()) {
      const encryptedStatements = await this.encryptStatements(statements);
      await this.bankCacheRepo.saveRange(source, accountId, fromTimestamp, toTimestamp, encryptedStatements);
    } else {
      await this.bankCacheRepo.saveRange(source, accountId, fromTimestamp, toTimestamp, statements);
    }
  }

  /**
   * Check coverage and return decrypted and merged statements if available.
   */
  async checkCoverage<TStatement extends CachedBankStatement>(
    source: string,
    accountId: string,
    from: number,
    to: number,
  ): Promise<TStatement[] | null> {
    const cachedEntries = await this.bankCacheRepo.checkCoverage(source, accountId, from, to);

    if (!cachedEntries || cachedEntries.length === 0) {
      return null;
    }

    const allStatements: CachedBankStatement[] = [];
    let hadDecryptionFailure = false;

    for (const entry of cachedEntries) {
      if (entry.statements) {
        allStatements.push(...entry.statements);
      } else if (entry.encryptedStatements) {
        try {
          const decryptedStatements = await this.decryptStatements(entry.encryptedStatements);
          allStatements.push(...decryptedStatements);
        } catch (error) {
          hadDecryptionFailure = true;
          console.warn(
            `[EncryptedBankCacheAdapter] Failed to decrypt entry ${entry.id}; removing it from cache and forcing API refresh`,
            {
              source,
              accountId,
              from,
              to,
              entryId: entry.id,
              fetchedAt: entry.fetchedAt,
              likelyCause: 'cache was encrypted with a different DEK',
            },
            error,
          );

          try {
            await this.bankCacheRepo.delete(entry.id);
          } catch (deleteError) {
            console.warn(
              `[EncryptedBankCacheAdapter] Failed to remove undecryptable cache entry ${entry.id}`,
              {
                source,
                accountId,
                from,
                to,
                entryId: entry.id,
              },
              deleteError,
            );
          }
        }
      }
    }

    if (hadDecryptionFailure) {
      return null;
    }

    if (allStatements.length === 0) {
      // The repository confirmed full coverage for this range — trust the cache.
      // Returning [] instead of null prevents a redundant API call.
      // Staleness is handled by the 7-day TTL in the repository.
      return [];
    }

    const filteredStatements = allStatements.filter((stmt) => stmt.time >= from && stmt.time <= to);
    const uniqueStatements = Array.from(new Map(filteredStatements.map((stmt) => [stmt.id, stmt])).values());
    uniqueStatements.sort((a, b) => a.time - b.time);

    return uniqueStatements as TStatement[];
  }

  /**
   * Delete expired cache entries.
   */
  async deleteExpired(): Promise<number> {
    return this.bankCacheRepo.deleteExpired();
  }
}
